import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import i18next from 'i18next';
import EAAccordion from '../../../../components/EAAccordion';
import AddingButton from '../../../../components/thirdparties/AddingButton';
import { Context } from '../../../../states/Store';
import {
  getContextInstances,
  getContextThirdpartyByTPid,
} from '../../../../services/edge/ContexteService';
import { useHistory } from 'react-router';
import EADataGrid from '../../../../components/common/datatable/EADataGrid';
import { contextInstanceListColumns } from './ContextInstanceListColumns';
import { useGridApiRef } from '@mui/x-data-grid';
import DossiersDataTable from '../../../../components/dossiers/DossiersDataTable';
import style from './style';
import ROUTES from '../../../../config/Routes';
import { PrivilegeEnum, RoleBasedAccessControlContext } from '../../../../services/common/RolesUtils';


const ThirdpartyContext = ({thirdpartyId, dossiers}) => {
  const {classes} = style();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = React.useContext(RoleBasedAccessControlContext);

  const [contextInstances, setContextInstances] = useState();
  const [contextThirdparties, setContextThirdparties] = useState();
  const [concernedContext, setConcernedContext] = useState();
  const [, setFilterRowCount] = useState(0);
  const [isExpanded, setExpanded] = React.useState(true);
  const history = useHistory();
  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const apiRef = useGridApiRef();
  const ref = useRef();

  const fetchContextInstances = useCallback(async () => {
    const instances = await getContextInstances(
      state.account.id,
      true,
      dispatch
    );
    setContextInstances(instances.content);
  }, [state.account.id, dispatch]);

  const fetchContextThirdparties = useCallback(async () => {
    const ctxThirdparties = await getContextThirdpartyByTPid(
      state.account.id,
      thirdpartyId,
      dispatch
    );
    setContextThirdparties(ctxThirdparties);
  }, [state.account.id, dispatch, thirdpartyId]);

  useEffect(() => {
    fetchContextInstances();
    fetchContextThirdparties();
  }, [dossiers, fetchContextInstances, fetchContextThirdparties]);

  useEffect(() => {
    if (contextInstances && contextThirdparties && dossiers) {
      const contextInstancesById = Object.fromEntries(
        contextInstances.map((instance) => [instance.id, instance])
      );

      const concernedContext = contextThirdparties
        .map((tp) => ({
          ...tp,
          instanceInfo: contextInstancesById[tp.ctxInstanceId] || null,
          dossiers: dossiers.filter((dossier) => tp.dossierIds.includes(dossier.dossierId))
        }))
        .filter((c) => c.instanceInfo);
      setConcernedContext(concernedContext);
    }
  }, [contextInstances, contextThirdparties, dossiers]);

  const showContextDetail = ({ ctxInstanceId, id }) => {
    const query = new URLSearchParams({
      instance: ctxInstanceId,
      thirdparty: id,
    }).toString();

    const url = `/${state.account.id}/${ROUTES.CONTEXT}?${query}`;
    window.open(url, '_blank');
  };

  const showDossiers = (id) => {
    setExpandedRowIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [id] // open one at a time
    );
  };


  const getDetailPanelContent = ({ row }) => {
    return (row.dossiers.length?<div style={{ padding: 16, minHeight: '107px' }}>
      <DossiersDataTable
        dossiers={row.dossiers}
        monitored
        component="thirdparty"
        pageSize={10}
        multiDataGridPage
        noFilter={true}
        pagination={false}
        hideFooter={true}
        customSx={{padding: 0}}
        onDeactivation={() =>fetchContextThirdparties()}
        fromTp
      />
    </div>:<></>)
  };
  const getDetailPanelHeight = () => 'auto';
  const getEstimatedRowHeight = () => 500;

  const addContext = async () => {
    history.push(`/${state.account.id}/${ROUTES.TP_SHEET_ADD_TP}?thirdpartyId=${thirdpartyId}`)
  };

  useEffect(() => {
    setExpanded(concernedContext?.length);
  }, [concernedContext]);

  return (
    <EAAccordion
      title={i18next.t('menu.context')}
      logo="/thirdparty/contexte"
      counter={concernedContext?.length}
      totalCount={concernedContext?.length}
      opened={isExpanded}
      buttons={[
        <div ref={ref}>
          <AddingButton
            label={i18next.t('context.addContextInstance')}
            handleClick={() => addContext(thirdpartyId)}
            disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT)}
          />
        </div>
      ]}
      disabled={!hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTEXT)}
    >
      {concernedContext && (
        <EADataGrid
          apiRef={apiRef}
          loading={false}
          inputRows={concernedContext}
          getRowId={(row) => row.id}
          columns={contextInstanceListColumns(showContextDetail, showDossiers, classes, expandedRowIds)}
          customSx={{
            '& .MuiDataGrid-columnHeader:nth-child(2)': { color: '#7E7E8F' },
          }}
          onStateChange={(e) => {
            setFilterRowCount(e.pagination.rowCount);
          }}
          paginationMode={'client'}
          hasCustomPagination={false}
          pageSize={10}
          variant={"sheet"}
          multiDataGridPage={true}
          getDetailPanelContent={getDetailPanelContent}
          getDetailPanelHeight={getDetailPanelHeight}
          getEstimatedRowHeight={getEstimatedRowHeight}
          getRowSpacing={() => ({ top: 0, bottom: 0 })}
          hideFooterSelectedRowCount={true}
          disableRowExpansion={true}
          getDetailPanelToggleColumnVisibility={() => false}
          detailPanelExpandedRowIds={expandedRowIds}
          onRowClick={(row) => {
            const {ctxInstanceId, id} = row.row;
            showContextDetail({ctxInstanceId, id})
          }}
          clickable={true}
        />
      )}
    </EAAccordion>
  );
};

export default ThirdpartyContext;
